import {isNotNil} from 'ramda';

import {invalidateOnSuccess, metadaApi} from '@dms/api/base';
import {BaseVoidResponseSchema} from '@dms/api/shared';

import {
  BulkDeleteDirectSaleVariantRequest,
  BulkDeleteDirectSaleVariantRequestSchema,
  BulkDeleteDirectSaleVariantResponse,
  PostDirectSaleVariantRequest,
  PostDirectSaleVariantRequestSchema,
  PostDirectSaleVariantResponse,
  PostDirectSaleVariantResponseSchema,
  GetDirectSaleTypesRequest,
  GetDirectSaleTypesResponse,
  GetDirectSaleTypesResponseSchema,
  GetDirectSaleVariantRequest,
  GetDirectSaleVariantRequestSchema,
  GetDirectSaleVariantResponse,
  GetDirectSaleVariantResponseSchema,
  GetDirectSaleVariantsRequest,
  GetDirectSaleVariantsResponse,
  GetDirectSaleVariantsResponseSchema,
  PatchDirectSaleVariantRequest,
  PatchDirectSaleVariantRequestSchema,
  PatchDirectSaleVariantResponse,
  GetDirectSaleTypesRequestSchema,
  GetDirectSaleVariantsRequestSchema,
  PatchDirectSaleVariantResponseSchema,
  GetDirectSaleVariantDefaultVarehousesPriorityResponse,
  GetDirectSaleVariantDefaultVarehousesPriorityRequest,
  GetDirectSaleVariantDefaultVarehousesPriorityRequestSchema,
  GetDirectSaleVariantDefaultVarehousesPriorityResponseSchema,
} from './types';

export const metadaWarehouseDirectSaleVariantApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getDirectSaleTypes: build.query<GetDirectSaleTypesResponse, GetDirectSaleTypesRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale-types`,
        method: 'GET',
        queryArg,
      }),
      providesTags: ['directSaleTypes'],
      extraOptions: {
        requestSchema: GetDirectSaleTypesRequestSchema,
        responseSchema: GetDirectSaleTypesResponseSchema,
      },
    }),
    getDirectSaleVariants: build.query<
      GetDirectSaleVariantsResponse,
      GetDirectSaleVariantsRequest | void
    >({
      query: (queryArg) => ({
        url: `warehouse/v2/direct-sale-variants`,
        method: 'GET',
        params: isNotNil(queryArg?.variantChangeDirectSaleId)
          ? {variantChangeDirectSaleId: queryArg?.variantChangeDirectSaleId}
          : undefined,
        headers: isNotNil(queryArg?.branchId) ? {'X-Branch': queryArg.branchId} : undefined,
        queryArg,
      }),
      providesTags: ['directSaleVariants'],
      extraOptions: {
        requestSchema: GetDirectSaleVariantsRequestSchema,
        responseSchema: GetDirectSaleVariantsResponseSchema,
      },
    }),
    getDirectSaleVariant: build.query<GetDirectSaleVariantResponse, GetDirectSaleVariantRequest>({
      query: (queryArg) => ({
        url: `warehouse/v5/direct-sale-variant/${queryArg.directSaleVariantId}`,
        method: 'GET',
        headers: isNotNil(queryArg.branchId) ? {'X-Branch': queryArg.branchId} : undefined,
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'directSaleVariants', id: queryArg.directSaleVariantId},
      ],
      extraOptions: {
        requestSchema: GetDirectSaleVariantRequestSchema,
        responseSchema: GetDirectSaleVariantResponseSchema,
      },
    }),
    postDirectSaleVariant: build.mutation<
      PostDirectSaleVariantResponse,
      PostDirectSaleVariantRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v7/direct-sale-variant`,
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['directSaleVariants']),
      extraOptions: {
        requestSchema: PostDirectSaleVariantRequestSchema,
        responseSchema: PostDirectSaleVariantResponseSchema,
      },
    }),
    patchDirectSaleVariant: build.mutation<
      PatchDirectSaleVariantResponse,
      PatchDirectSaleVariantRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v7/direct-sale-variant/${queryArg.directSaleVariantId}`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: PatchDirectSaleVariantRequest) => [
        {type: 'directSaleVariants', id: queryArg.directSaleVariantId},
        'directSaleVariants',
      ]),
      extraOptions: {
        requestSchema: PatchDirectSaleVariantRequestSchema,
        responseSchema: PatchDirectSaleVariantResponseSchema,
      },
    }),
    bulkDeleteDirectSaleVariant: build.mutation<
      BulkDeleteDirectSaleVariantResponse,
      BulkDeleteDirectSaleVariantRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale-variant/delete`,
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: ['directSaleVariants'],
      extraOptions: {
        requestSchema: BulkDeleteDirectSaleVariantRequestSchema,
        responseSchema: BaseVoidResponseSchema,
      },
    }),
    getDirectSaleVariantDefaultVarehousesPriority: build.query<
      GetDirectSaleVariantDefaultVarehousesPriorityResponse,
      GetDirectSaleVariantDefaultVarehousesPriorityRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/direct-sale-variant/default-warehouse-priority`,
        method: 'GET',
        queryArg,
      }),
      providesTags: ['directSaleVariantDefaultVarehousesPriority'],
      extraOptions: {
        requestSchema: GetDirectSaleVariantDefaultVarehousesPriorityRequestSchema,
        responseSchema: GetDirectSaleVariantDefaultVarehousesPriorityResponseSchema,
      },
    }),
  }),
});

export const {
  useGetDirectSaleTypesQuery,
  useGetDirectSaleVariantsQuery,
  useGetDirectSaleVariantQuery,
  usePostDirectSaleVariantMutation,
  usePatchDirectSaleVariantMutation,
  useBulkDeleteDirectSaleVariantMutation,
  useGetDirectSaleVariantDefaultVarehousesPriorityQuery,
} = metadaWarehouseDirectSaleVariantApi;
